import apiClient from '@/lib/api-client';

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'admin' | 'manager' | 'employee' | 'customer';
  isActive: boolean;
  gln: string | null;
  language: string;
  createdAt: string;
  /** Newest session moment (ISO) or null; only the list endpoint sends it. */
  lastActiveAt?: string | null;
}

export interface CreateUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  role: 'admin' | 'manager' | 'employee' | 'customer';
  gln?: string;
  language?: string;
}

export interface UpdateUserRequest {
  firstName?: string;
  lastName?: string;
  role?: 'admin' | 'manager' | 'employee' | 'customer';
  gln?: string;
  isActive?: boolean;
  language?: string;
}

const USERS_PAGE_SIZE = 100;
/** Safety cap: at most 50 requests (5,000 users) even if the server keeps reporting a larger total. */
const USERS_MAX_PAGES = 50;

/**
 * The whole user list, fetched 100 at a time (spec-tabelfilters §8): `/users` without parameters
 * returns only the first 20. Stops at a short or empty page, or once the reported total is reached.
 */
export async function getUsers(): Promise<{ data: User[] }> {
  // Keyed by id: the server pages by a non-unique name with OFFSET, so a user can show up on two pages.
  const perId = new Map<string, User>();
  for (let page = 1; page <= USERS_MAX_PAGES; page++) {
    const response = await apiClient.get<{ data: User[]; meta?: { total?: number } }>('/users', {
      params: { page, pageSize: USERS_PAGE_SIZE },
    });
    const batch = response.data.data ?? [];
    for (const user of batch) perId.set(user.id, user);
    const total = response.data.meta?.total;
    if (batch.length < USERS_PAGE_SIZE || (total !== undefined && page * USERS_PAGE_SIZE >= total)) break;
  }
  return { data: [...perId.values()] };
}

export async function getUser(userId: string): Promise<{ data: User }> {
  const response = await apiClient.get<{ data: User }>(`/users/${userId}`);
  return response.data;
}

export async function createUser(data: CreateUserRequest): Promise<{ data: User }> {
  const response = await apiClient.post<{ data: User }>('/users', data);
  return response.data;
}

export async function updateUser(
  userId: string,
  data: UpdateUserRequest,
): Promise<{ data: User }> {
  const response = await apiClient.patch<{ data: User }>(
    `/users/${userId}`,
    data,
  );
  return response.data;
}

export async function deactivateUser(userId: string): Promise<void> {
  await apiClient.patch(`/users/${userId}/deactivate`);
}

export async function checkEmail(email: string): Promise<{ data: { exists: boolean } }> {
  const response = await apiClient.get<{ data: { exists: boolean } }>(
    `/users/check-email?email=${encodeURIComponent(email)}`,
  );
  return response.data;
}
